import { Button, Card, Divider, Grid, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useApplicationId } from '../../../hooks/useApplicationId';
import { usePreworkMentorApplicationsList } from './usePreworkMentorApplicationsList';
import { Filter } from './Filter';
import { ApplicationsList } from './ApplicationsList';
import { useCallback } from 'react';
import {
	ApplicationSummary,
	UserRole,
} from '../../../shared/VolunteerApplicationServiceTypes';
import {
	FUNCTIONARY_HOME,
	PRE_WORK_INTERVIEW,
	PRE_WORK_MENTOR_CONTEXT,
} from '../../../shared/Routes';
import moment from 'moment';
import Icon from '../../../components/ui-components/Icon';
import LeftArrowWhite from '../../../assets/images/arrow-left-white.svg';
import { useBatchId } from '../../../hooks/useBatchId';
import useUserSession from '../../../hooks/useUserSession';
import { hasPrivilege } from '../../../shared/Utils';
import { PrivilegeCodes } from '../../../shared/RoleConstants';
import { NoAccess } from '../../../components/NoAccess';
import EmptyList from '../../../components/EmptyList';

const { Text } = Typography;
const { useBreakpoint } = Grid;

export const PreworkMentorApplicationsListContainer = () => {
	const {
		applicationsList,
		onPageChange,
		filter,
		onFilterChange,
		paging,
		selectedBatch,
	} = usePreworkMentorApplicationsList();
	const { navigateWithApplicationId } = useApplicationId();
	const { navigateToRoute } = useBatchId();
	const { t } = useTranslation();
	const { selectedRole, loggedInUserPrivileges } = useUserSession();
	const screens = useBreakpoint();
	const isMobile = !screens.sm;
	const resultDate = selectedBatch?.snpResultDateForCandidate;
	const batchName = selectedBatch?.name;
	const isPreworkMentor = selectedRole === UserRole.PREWORK_MENTOR;

	const showProposerLoadPrivilege = hasPrivilege(
		PrivilegeCodes.GET_PROPOSER_LOAD_SUMMARY,
		loggedInUserPrivileges
	);

	const showEligibleCandidates = hasPrivilege(
		PrivilegeCodes.GET_ELIGIBLE_CANDIDATES_FOR_NEXT_BATCH,
		loggedInUserPrivileges
	);

	const showEvaluate = hasPrivilege(
		PrivilegeCodes.GET_APPLICATION,
		loggedInUserPrivileges
	);

	const showZonesProposerLoad = hasPrivilege(
		PrivilegeCodes.GET_ZONES_PROPOSER_LOAD_SUMMARY,
		loggedInUserPrivileges
	);

	const showViewCandidate = hasPrivilege(
		PrivilegeCodes.GET_CANDIDATES_LOAD_SUMMARY,
		loggedInUserPrivileges
	);

	const showViewScreeningDashboard = hasPrivilege(
		PrivilegeCodes.GET_CANDIDATE_SCREENING_PROGRESS_SUMMARY,
		loggedInUserPrivileges
	);

	const showProposerReassign = hasPrivilege(
		PrivilegeCodes.REASSIGN_FUNCTIONARY_TO_APPLICATION,
		loggedInUserPrivileges
	);

	const showUpdateRegion = hasPrivilege(
		PrivilegeCodes.UPDATE_REGION_ZONE_CENTER_OF_APPLICATION,
		loggedInUserPrivileges
	);

	const showEvaluatePreworkApplication = hasPrivilege(
		PrivilegeCodes.CAPTURE_PREWORK_MENTOR_ANSWERS,
		loggedInUserPrivileges
	);

	const showCentralTeamDashboardPrivilege = hasPrivilege(
		PrivilegeCodes.GET_CENTRAL_TEAM_DASHBOARD_DETAIL,
		loggedInUserPrivileges
	);

	const showPreworkPrivilege = hasPrivilege(
		PrivilegeCodes.GET_PREWORK_APPLICATIONS,
		loggedInUserPrivileges
	);

	const showNoAccess =
		showEligibleCandidates ||
		showEvaluate ||
		showProposerLoadPrivilege ||
		showZonesProposerLoad ||
		showViewCandidate ||
		showViewScreeningDashboard ||
		showProposerReassign ||
		showUpdateRegion ||
		showCentralTeamDashboardPrivilege ||
		showPreworkPrivilege ||
		showEvaluatePreworkApplication;

	const handleEvaluateClick = useCallback(
		(application: ApplicationSummary) => {
			if (isPreworkMentor) {
				navigateWithApplicationId(
					PRE_WORK_MENTOR_CONTEXT,
					application.applicationId
				);
			} else {
				navigateWithApplicationId(
					PRE_WORK_INTERVIEW,
					application.applicationId
				);
			}
		},
		[isPreworkMentor, navigateWithApplicationId]
	);

	const handleBackClick = useCallback(() => {
		navigateToRoute(FUNCTIONARY_HOME);
	}, [navigateToRoute]);

	const filterComponent = filter && (
		<div className="mb-[16px]">
			<Filter value={filter} onFilterChange={onFilterChange} />
		</div>
	);

	const isAfter90Days = resultDate
		? moment().isAfter(moment(resultDate).add(90, 'days'))
		: false;

	const formattedResultDate = resultDate
		? moment(resultDate).add(90, 'days').format('Do MMM, YYYY')
		: '';

	if (isAfter90Days) {
		return (
			<Card className="w-full md:w-[50%] mx-auto mt-10 px-6 py-6 pt-2 font-sans shadow-lg">
				<div className="flex flex-col items-center text-center space-y-4">
					<div className="text-red-600 font-semibold text-lg">
						{t('preworkConfirmationTimelineEnded')}
					</div>
					<Divider />
					<Text className="text-base text-gray-700">
						{t('thePreworkEvaluationPeriodHasEnded', {
							batchName,
							resultDate: formattedResultDate,
						})}
					</Text>
					{!isPreworkMentor && (
						<Button
							type="primary"
							onClick={handleBackClick}
							className="font-bold text-sm !w-40"
						>
							<span className="flex gap-2 items-center justify-center">
								<Icon src={LeftArrowWhite} width="w-4" height="h-4" />
								{t('back')}
							</span>
						</Button>
					)}
				</div>
			</Card>
		);
	}

	if (!showNoAccess) {
		return <NoAccess />;
	}

	return (
		<div className="flex flex-col p-[16px]">
			<div className="flex gap-8">
				<Text className="hidden md:block font-sans font-bold text-xl text-secondary-black mb-4 whitespace-nowrap">
					{isPreworkMentor
						? t('listOfCandidates')
						: t('listOfPreworkCandidates')}
				</Text>
			</div>
			<div className={isMobile ? '' : 'flex items-center justify-between'}>
				{filterComponent}
			</div>
			{showPreworkPrivilege ? (
				<ApplicationsList
					data={applicationsList}
					onPageChange={onPageChange}
					onEvaluateClick={handleEvaluateClick}
					pageConfig={paging}
				/>
			) : (
				<EmptyList message={t('noApplications')} />
			)}
		</div>
	);
};
